import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { userAPI } from '../services/userAPI';
import { pricingAPI } from '../services/pricingAPI';

export const useUserManagement = (user) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingUserId, setProcessingUserId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'user'
  });

  // Función para aprobar solicitud de cuota
  const handleApproveQuota = async (paymentId, setShowPendingModal) => {
    try {
      await pricingAPI.approveQuotaRequest(paymentId);
      await fetchUsers();
      if (setShowPendingModal) setShowPendingModal(false);
      toast.success('Solicitud aprobada correctamente');
    } catch (error) {
      toast.error('Error al aprobar cuota');
    }
  };

  // Función para rechazar solicitud de cuota
  const handleRejectQuota = async (paymentId, setShowPendingModal) => {
    try {
      await pricingAPI.rejectQuotaRequest(paymentId);
      await fetchUsers();
      if (setShowPendingModal) setShowPendingModal(false);
      toast.success('Solicitud rechazada correctamente');
    } catch (error) {
      toast.error('Error al rechazar cuota');
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const result = await userAPI.getAllUsersWithQuotas();
      if (result.success) {
        const validatedUsers = userAPI.processUsersResponse(result.data);
        setUsers(validatedUsers);
      } else {
        // Manejar errores específicos
        if (result.statusCode === 403) {
          toast.error('Acceso denegado. No tienes permisos de administrador.');
          navigate('/');
        } else if (result.statusCode === 404) {
          toast.error('Endpoint no encontrado. Contacta al soporte técnico.');
          setUsers([]);
        } else {
          toast.error(`Error del servidor: ${result.error}`);
          setUsers([]);
        }
      }
    } catch (error) {
      toast.error('Error de conexión con el servidor. Verifica tu conexión a internet.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Verificar que el usuario sea administrador
  useEffect(() => {
    const checkUserRole = setTimeout(() => {
      if (!user) {
        return;
      }
      
      if (user.role !== 'admin') {
        toast.error('Acceso denegado. Solo administradores pueden acceder a esta página.');
        navigate('/');
        return;
      }
      
      fetchUsers();
    }, 100);

    return () => clearTimeout(checkUserRole);
  }, [user, navigate, fetchUsers]);

  return {
    users,
    loading,
    processingUserId,
    setProcessingUserId,
    showCreateModal,
    setShowCreateModal,
    newUser,
    setNewUser,
    fetchUsers
    ,handleApproveQuota
    ,handleRejectQuota
  };
};

export const useUserActions = (users, token, fetchUsers, setProcessingUserId, setShowCreateModal, setNewUser) => {
  const toggleUserStatus = async (userId, currentStatus, currentUser) => {
    if (userId === currentUser.id) {
      toast.error('No puedes cambiar tu propio estado');
      return;
    }
    try {
      setProcessingUserId(userId);
      const result = await userAPI.toggleUserStatus(userId, !currentStatus);
      if (result.success) {
        toast.success(result.data.message);
        fetchUsers();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Error de conexión al cambiar estado del usuario');
    } finally {
      setProcessingUserId(null);
    }
  };

  const createUser = async (e, newUser) => {
    e.preventDefault();
    try {
      const result = await userAPI.createUser(newUser);
      if (result.success) {
        toast.success('Usuario creado exitosamente');
        setShowCreateModal(false);
        setNewUser({ email: '', full_name: '', password: '', role: 'user' });
        fetchUsers();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Error de conexión al crear usuario');
    }
  };

  return {
    toggleUserStatus,
    createUser
  };
};

export const useUserUtils = () => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const badgeClass = role === 'admin' 
      ? 'badge bg-danger' 
      : 'badge bg-primary';
    
    return (
      <span className={badgeClass}>
        {role === 'admin' ? 'Administrador' : 'Usuario'}
      </span>
    );
  };

  const getStatusBadge = (isActive) => {
    const badgeClass = isActive 
      ? 'badge bg-success' 
      : 'badge bg-secondary';
    
    return (
      <span className={badgeClass}>
        {isActive ? 'Activo' : 'Inactivo'}
      </span>
    );
  };

  return {
    formatDate,
    getRoleBadge,
    getStatusBadge
  };
};
